import { useActiveSlideDeckPublicKey } from '@mentimeter/core-hooks/use-ids';
import { usePresentationRealtimeStore } from '@mentimeter/realtime';
import useSWR from 'swr';
import { validate as validateUuid } from 'uuid';
import {
  makeRequest,
  type ApiRequestParams,
} from '../request-wrapper/make-request';
import { POLLING_INTERVAL } from './constants';
import { getRegion } from './get-region';

// Region and userAuth are handled by makeRequest
type ApiSpecificParams<T> = Omit<T, 'region' | 'userAuth'>;

interface FetchResultsConfig<TRequestParams, TResponse> {
  requestParams: ApiSpecificParams<TRequestParams>;
  cacheKey: string | null;
  shouldFetchResults: boolean;
  sessionId: number | undefined;
  getRequest: (params: TRequestParams) => Request;
  getResponse: (response: Response) => Promise<TResponse>;
}

const areValidRequestParams = (requestParams: ApiSpecificParams<any>) => {
  if (
    'interactiveContentId' in requestParams &&
    !validateUuid(requestParams.interactiveContentId)
  ) {
    return false;
  }
  if ('adminKey' in requestParams && !requestParams.adminKey) {
    return false;
  }
  return true;
};

export const useFetchResultsBase = <TRequestParams, TResponse>({
  requestParams,
  cacheKey,
  shouldFetchResults,
  sessionId,
  getRequest,
  getResponse,
}: FetchResultsConfig<TRequestParams, TResponse>) => {
  const shouldMakeRequest =
    shouldFetchResults && areValidRequestParams(requestParams);
  const slideDeckPublicKey = useActiveSlideDeckPublicKey();
  const connectionState = usePresentationRealtimeStore(
    (state) => state.connectionState,
  );

  const { data, mutate, ...rest } = useSWR(
    shouldMakeRequest ? cacheKey : null,
    async () => {
      const region = getRegion(slideDeckPublicKey);
      const response = await fetch(
        makeRequest(getRequest, {
          ...requestParams,
          ...(region ? { region } : {}),
          ...(sessionId !== undefined ? { session: sessionId } : {}),
        } as ApiRequestParams<TRequestParams>),
      );
      return await getResponse(response);
    },
    {
      shouldRetryOnError: false,
      refreshInterval:
        connectionState === 'failed' || connectionState === 'disconnected'
          ? POLLING_INTERVAL
          : 0,
    },
  );

  return {
    data: shouldMakeRequest ? data : undefined,
    mutate,
    ...rest,
  };
};
